import _ from "lodash";
import $ from "jquery";
import * as d3 from 'd3';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "semantic-ui-react";
import { LimitData, Measurement, MeasurementData, VectorData } from "../../models/testing_model";
import { PlotChannelColors, PlotChannelDeepColors } from "../../models/common_model";
import { TestResultMissingPointsContext } from "../../context";


export function VectorPlotComponent(props: {
    measurement: Measurement,
    data: MeasurementData[],
    height: number
}) {
    const currentInfo = useState<{
        x: number,
        y: number[],
        upper: number[],
        lower: number[],
        outOfLimit: number[],
        isShow: boolean
    }>({ x: 0, y: [0, 0], upper: [0, 0], lower: [0, 0], outOfLimit: [0, 0], isShow: false });

    const [chartHeight, setChartHeight] = useState(0);

    const elementRef = useRef<HTMLDivElement>(null);
    const { measurement, data: allData } = props;

    const sIsMissingPoints = useContext(TestResultMissingPointsContext);

    const [data, setData] = useState<MeasurementData[]>(allData);
    const previousIndexes = _.uniq(allData.map(d => d.index)).sort();
    const currentAttempts = useState(_.max(previousIndexes));

    const channels = _.uniq(allData.filter(d => d.result && !_.isEmpty((d.result as VectorData).x_values)).map(d => d.channel)).sort();
    const sSelectedChannel = useState<boolean[]>(channels.map(c => true));

    const channelColors: string[][] = channels.map((channel, index) => [PlotChannelColors[index], PlotChannelDeepColors[index]]);

    const selectedChannelColors = channelColors.filter((c, index) => sSelectedChannel[0][index]);

    const margin = ({ top: 20, right: 30, bottom: 40, left: 60 });

    useEffect(() => {
        setData(props.data.filter(d => d.index === currentAttempts[0]).map(d => {
            const result = d.result as VectorData;
            const newXValues: number[] = [];
            const newYValues: number[] = [];
            if (result.y_values) {
                result.y_values.forEach((y, i) => {
                    if (_.isNumber(y)) {
                        newXValues.push(result.x_values[i]);
                        newYValues.push(y);
                    }
                });
    
                return {...d, result: {x_values: newXValues, y_values: newYValues}};
            } else {
                return d;
            }
        }));
    }, [props.data, currentAttempts[0]]);

    useLayoutEffect(() => {
        const $nextElem = $(elementRef.current!).next();
        if ($nextElem.length > 0) {
            const checkUntilStable = (prev: number) => {
                const current = $nextElem.offset()!.top;

                if (prev === current) {
                    setChartHeight(props.height - $nextElem.outerHeight()! - $(elementRef.current!).prev().outerHeight()! - 14 * 3);
                } else {
                    setTimeout(() => checkUntilStable(current), 200);
                }
            }

            checkUntilStable(0);
        }
    }, [props.height]);

    useEffect(() => {
        if (chartHeight == 0) {
            return;
        }
        const selectedChannels = channels.filter((channel, index) => sSelectedChannel[0][index]);
        if (selectedChannels.length === 0) {
            return;
        }

        const height = chartHeight;
        const width = elementRef.current!.offsetWidth;

        const svg = d3.select(elementRef.current!)
            .append("svg")
            .attr("viewBox", [0, 0, width, height] as any)
            .style('width', width + 'px')
            .style('height', height + 'px');


        const firstChannelData = data.find(d => selectedChannels.includes(d.channel))!;

        const xValues = (firstChannelData.result as VectorData).x_values;
        if (!xValues) {
            return;
        }
        const xDomain = d3.extent(xValues) as number[];
        const xScale = xDomain[1] > 1000 ? d3.scaleLog : d3.scaleLinear;
        const x = xScale().domain(xDomain).range([margin.left, width - margin.right]);
        const xAxis = (g: any) => {
            let dataCall = d3.axisBottom(x as any);
            if (xDomain[1] >= 1000) {
                dataCall = dataCall.ticks(null, 'd');
            } else {
                dataCall = dataCall.tickFormat(((d: number) => d.toFixed(1)) as any);
            }

            dataCall = dataCall.tickSizeInner(-height + margin.top + margin.bottom);

            g.attr("transform", `translate(0,${height - margin.bottom})`)
                .call(dataCall);
        }

        svg.append("g")
            .attr("class", "x-axis-inner")
            .call(xAxis);


        let yDomain: number[] = [];
        selectedChannels.forEach((channel, index) => {
            const channelData = data.find(d => d.channel == channel)!;
            yDomain = yDomain.concat((channelData.result as VectorData).y_values);
            if (channelData.limits.lower) {
                yDomain = yDomain.concat((channelData.limits.lower as VectorData).y_values);
            }
            if (channelData.limits.upper) {
                yDomain = yDomain.concat((channelData.limits.upper as VectorData).y_values);
            }
            yDomain = yDomain.filter(_.isNumber);
        });
        yDomain = d3.extent(yDomain) as number[];
        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top])

        const yAxis = (g: any) => {
            g.attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(y).tickSizeInner(-width + margin.left + margin.right));
        }

        svg.append("g")
            .attr("class", "y-axis-inner")
            .call(yAxis);

        const line = (d3.line() as any)
            .x(function (d: number[], i: number) { return x(d[0]) })
            .y(function (d: number[], i: number) { return y(d[1]) });

        const channelLimits: {lower: number[], upper: number[]}[] = []
        const channelValues: number[][] = [];

        const outOfLimitPoints: number[][] = [];

        selectedChannels.forEach((channel, index) => {
            const channelData = data.find(d => d.channel == channel)!;

            const uppers: number[][] = [];
            const lowers: number[][] = [];
            const limits = channelData.limits as LimitData<VectorData>;
            const result = channelData.result as VectorData;
            const resultValues = result.x_values.map((x, i) => [x, result.y_values[i]]);
            const lowerFailPoints: number[][] = []
            const upperFailPoints: number[][] = []
            channelValues.push(result.y_values);

            channelLimits[index] = {lower: [], upper: []};
            

            if (limits) {
                if (limits.lower) {
                    const linearLowerScale = d3.scaleLinear().domain(limits.lower.x_values).range(limits.lower.y_values);
                    const minX = limits.lower.x_values[0];
                    const maxX = limits.lower.x_values[limits.lower.x_values.length - 1];
                    let lastPointStatus = true;
                    xValues.forEach((x, i) => {
                        let pointStatus = true;
                        if (x >= minX && x <= maxX) {
                            const l = linearLowerScale(x);
                            if (result.y_values[i] < l) {
                                pointStatus = false;
                            }
                            channelLimits[index].lower.push(l);
                        } else {
                            channelLimits[index].lower.push(NaN);
                        }

                        if (!pointStatus && (lastPointStatus || i === xValues.length - 1)) {
                            lowerFailPoints.push([x, result.y_values[i]]);
                        } else if (pointStatus && !lastPointStatus) {
                            lowerFailPoints.push([xValues[i - 1], result.y_values[i - 1]]);
                        }

                        lastPointStatus = pointStatus;

                    });

                    limits.lower.x_values.forEach((x, i) => {
                        limits.lower?.y_values[i] && lowers.push([x, limits.lower?.y_values[i]]);
                    });
                }

                if (limits.upper) {
                    const linearUpperScale = xScale().domain(limits.upper.x_values).range(limits.upper.y_values);
                    const minX = limits.upper.x_values[0];
                    const maxX = limits.upper.x_values[limits.upper.x_values.length - 1];
                    let lastPointStatus = true;

                    xValues.forEach((x, i) => {
                        let pointStatus = true;
                        if (x >= minX && x <= maxX) {
                            const u = linearUpperScale(x);
                            if (result.y_values[i] > u) {
                                pointStatus = false;
                            }
                            channelLimits[index].upper.push(u);
                        } else {
                            channelLimits[index].upper.push(NaN);
                        }

                        if (!pointStatus && (lastPointStatus || i === xValues.length - 1)) {
                            upperFailPoints.push([x, result.y_values[i]]);
                        } else if (pointStatus && !lastPointStatus) {
                            upperFailPoints.push([xValues[i - 1], result.y_values[i - 1]]);
                        }

                        lastPointStatus = pointStatus;
                    });

                    limits.upper.x_values.forEach((x, i) => {
                        limits.upper?.y_values[i] && uppers.push([x, limits.upper?.y_values[i]]);
                    });
                }
            }

            svg.append("path")
                .attr('class', 'data-line')
                .attr("fill", "none")
                .attr("stroke", selectedChannelColors[index][0])
                .attr("stroke-width", 1)
                .attr("d", line(resultValues))

            const hasUpper = uppers.length > 0;
            if (hasUpper) {
                svg.append("path")
                    .attr('class', 'data-line')
                    .attr("fill", "none")
                    .attr("stroke", '#FF0000')
                    .attr("stroke-width", 1)
                    .attr("d", line(uppers))
            }

            const hasLower = lowers.length > 0
            if (hasLower) {
                svg.append("path")
                    .attr('class', 'data-line')
                    .attr("fill", "none")
                    .attr("stroke", '#FF0000')
                    .attr("stroke-width", 1)
                    .attr("d", line(lowers))
            }

            if (lowerFailPoints.length > 0) {
                for (const p of lowerFailPoints) {
                    const xv = x(p[0]);
                    const yv = y(p[1]);
                    svg.append('polygon')
                    .attr('class', 'limit-triangle')
                    .attr("fill", 'red')
                    .attr("stroke", '#999')
                    .attr("points", `${xv},${yv} ${xv - 5},${yv + 10} ${xv + 5},${yv + 10}`);
                }
            }

            if (upperFailPoints.length > 0) {
                for (const p of upperFailPoints) {
                    const xv = x(p[0]);
                    const yv = y(p[1]);
                    svg.append('polygon')
                    .attr('class', 'limit-triangle')
                    .attr("fill", 'red')
                    .attr("stroke", '#999')
                    .attr("points", `${xv},${yv} ${xv - 5},${yv - 10} ${xv + 5},${yv - 10}`);
                }
            }

            sIsMissingPoints[1](measurement.measurement_status == 2 && lowerFailPoints.length == 0 && upperFailPoints.length == 0);
        });

        const xAxisTitle = xDomain[xDomain.length - 1] >= 1500 ? 'Frequency(Hz)' : '';

        svg.append('text')
            .attr('class', 'axis-label')
            .attr('x', x(xDomain[0]) + x(xDomain[1]) / 2 - x(xDomain[0]) / 2 - 120)
            .attr('y', height)
            .text(xAxisTitle)

        const unit = measurement.result_unit || measurement.limit_unit;
        const yAxisTitle = measurement.measurement_name + (unit ? ` (${unit})` : '');

        svg.append('text')
            .attr('class', 'axis-label')
            .attr('x', -(height / 2) - margin.left / 2 - margin.right / 2)
            .attr('y', (margin.top + margin.bottom) / 2 / 2.4)
            .attr("text-anchor", "start")
            .attr("transform", "rotate(-90)")
            .attr("title", yAxisTitle)
            .text(yAxisTitle)

        //focus
        const focus = svg.append('g').style('display', 'none');

        focus.append('line')
            .attr('id', 'focusLineX')
            .attr('class', 'focusLine');

        selectedChannels.forEach((channel, index) => {
            focus.append('circle')
                .attr('id', 'focusCircle_' + index)
                .attr('r', 5)
                .attr('class', 'circle focusCircle');

            focus.append('line')
                .attr('id', 'focusLineY_' + index)
                .attr('class', 'focusLine');
        });

        const bisectDate = d3.bisector(function (d) { return d }).left;
        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .on('mouseover', function () { focus.style('display', null); })
            .on('mouseout', function () {
                focus.style('display', 'none');
                currentInfo[1](info => ({ ...info, isShow: false }))
            })
            .on('mousemove', function (event) {
                const mouse = d3.pointer(event);
                const mouseX = x.invert(mouse[0]);
                let i = bisectDate(xValues, mouseX); // returns the index to the current data item

                const d0 = xValues[i - 1];
                const d1 = xValues[i];
                // work out which date value is closest to the mouse
                if (mouseX - d0 < d1 - mouseX) {
                    i -= 1;
                }

                if (!xValues[i] || !channelValues.find(vs => vs[i])) {
                    const emptyValues: number[] = selectedChannels.map(c => 0);
                    currentInfo[1](v => ({
                        isShow: false,
                        x: 0,
                        y: emptyValues,
                        lower: emptyValues,
                        upper: emptyValues,
                        outOfLimit: emptyValues
                    }))
                } else {
                    currentInfo[1](() => {
                        const yValue: number[] = [];
                        const lower: number[] = [];
                        const upper: number[] = [];
                        const outOfLimit: number[] = [];

                        selectedChannels.map((channel, index) => {
                            const channelData = data.find(d => d.channel == channel)!;
                            const channelResult = channelData.result as VectorData;
                            const y = channelResult.y_values[i];
                            const l = channelLimits[index].lower[i] || NaN;
                            const u = channelLimits[index].upper[i] || NaN;
                            yValue.push(y);
                            lower.push(l);
                            upper.push(u);

                            let outOfLimitValue = 0
                            if (!isNaN(l) && y < l) {
                                outOfLimitValue = l - y;
                            } else if (!isNaN(u) && y > u) {
                                outOfLimitValue = y - u
                            }

                            outOfLimit.push(outOfLimitValue);
                        })

                        return {
                            isShow: true,
                            x: xValues[i],
                            y: yValue,
                            lower,
                            upper,
                            outOfLimit
                        }
                    });

                    const xv = x(xValues[i]);

                    focus.select('#focusLineX')
                        .attr('x1', xv).attr('y1', y(yDomain[0]))
                        .attr('x2', xv).attr('y2', y(yDomain[1]));

                    selectedChannels.forEach((channel, index) => {
                        const yv = y(channelValues[index][i]);

                        focus.select('#focusCircle_' + index)
                            .attr('cx', xv)
                            .attr('cy', yv);

                        focus.select('#focusLineY_' + index)
                            .attr('x1', x(xDomain[0])).attr('y1', yv)
                            .attr('x2', x(xDomain[1])).attr('y2', yv);
                    });
                }
            });

        return () => {
            svg.remove()
        }

    }, [sSelectedChannel[0].join(','), measurement, data, chartHeight]);

    return <>
        <div style={{ width: '100%' }}>
            {previousIndexes.length > 1 && <>
                <Button.Group size="mini" compact>
                    {previousIndexes.map(i => <Button key={i} size="mini" basic={currentAttempts[0] !== i} onClick={e => currentAttempts[1](i)}>Attempts {i}</Button>)}
                </Button.Group>
            </>}
            <div style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 2rem' }}>
                    X: {currentInfo[0].x.toFixed(2)} &nbsp;&nbsp;
                    {channels.filter((channel, index) => sSelectedChannel[0][index]).map((channel, index) => <div key={channel} style={{ background: selectedChannelColors[index][1], padding: '0.5rem', color: '#ddd' }}>
                        <div style={{visibility: currentInfo[0].isShow ? 'visible' : 'hidden'}}>
                            <b style={{ color: '#fff' }}>CH{channel}:</b>&nbsp;&nbsp;
                            <span style={{ color: ((!isNaN(currentInfo[0].lower[index]) && currentInfo[0].y[index] < currentInfo[0].lower[index]) || (!isNaN(currentInfo[0].upper[index]) && currentInfo[0].y[index] >= currentInfo[0].upper[index])) ? 'red' : '' }}>Y: {currentInfo[0].y[index]?.toExponential(3)}</span> &nbsp;&nbsp;
                            {!isNaN(currentInfo[0].upper[index]) && <>Upper: {currentInfo[0].upper[index]?.toFixed(2)} &nbsp;&nbsp;</>}
                            {!isNaN(currentInfo[0].lower[index]) && <>Lower: {currentInfo[0].lower[index]?.toFixed(2)} &nbsp;&nbsp;</>}
                            Out Of Limit: {currentInfo[0].outOfLimit[index]?.toExponential(3)} &nbsp;&nbsp;
                        </div>
                    </div>)}
            </div>
            <div ref={elementRef} />
            <Button.Group size="mini" style={{ paddingLeft: `${margin.left}px` }}>
                {channels.map((channel, index) => <Button style={{ background: sSelectedChannel[0][index] ? channelColors[index][0] : null }} color={'grey'} onClick={() => sSelectedChannel[1](vs => vs.map((v, i) => index === i ? !v : v))} key={channel}>Channel{channel}</Button>)}
            </Button.Group>
        </div>
    </>
}